export type RemoteActivityFilter = 'all' | 'survey' | 'mission';

export const remoteMissionVisibilityDefaults = {
  onlyAcceptableMissions: false,
  showUpcomingMissions: true,
};

type RemoteBranchForDisplay = {
  name?: string | null;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  remaining?: number;
  availabilityBlocked?: boolean;
};

type RemoteBranchWithCoordinates = RemoteBranchForDisplay & {
  latitude?: number | null;
  longitude?: number | null;
};

export const hasRemoteBranchCoordinates = <T extends RemoteBranchWithCoordinates>(branch: T) =>
  Number.isFinite(branch.latitude) && Number.isFinite(branch.longitude);

export const getRemoteBranchMapViewport = <T extends RemoteBranchWithCoordinates>(branches: T[]) => {
  const geocodedBranches = branches.filter(hasRemoteBranchCoordinates);
  if (!geocodedBranches.length) return undefined;

  const latitudes = geocodedBranches.map((branch) => branch.latitude as number);
  const longitudes = geocodedBranches.map((branch) => branch.longitude as number);
  const north = Math.max(...latitudes);
  const south = Math.min(...latitudes);
  const east = Math.max(...longitudes);
  const west = Math.min(...longitudes);

  return {
    center: { lat: (north + south) / 2, lng: (east + west) / 2 },
    northEast: { lat: north, lng: east },
    southWest: { lat: south, lng: west },
    count: geocodedBranches.length,
  };
};

export const filterRemoteBranchesForShopper = <T extends RemoteBranchForDisplay>(
  branches: T[],
  requiresPhone: boolean,
) => branches.filter((branch) =>
  isRemoteBranchSelectable(branch) && (!requiresPhone || !!branch.phone?.trim()),
);

export interface RemoteMissionFilterOptions {
  activity: RemoteActivityFilter;
  selectedTagIds: readonly string[];
  onlyAcceptableMissions: boolean;
  showUpcomingMissions: boolean;
  surveyActivity: number;
  missionActivity: number;
}

export const filterAndSortRemoteMissions = <
  TMission extends {
    name?: string;
    brandName?: string;
    activity?: number;
    closingDate?: Date;
    upcomingPeriod?: boolean;
    upcomingPeriodStartDate?: Date;
    remaining?: number;
    tags?: { id?: string }[];
  },
>(missions: readonly TMission[], options: RemoteMissionFilterOptions): TMission[] => {
  return missions
    .filter((mission) => {
      if (options.activity === 'survey' && mission.activity !== options.surveyActivity) return false;
      if (options.activity === 'mission' && mission.activity !== options.missionActivity) return false;
      if (options.selectedTagIds.length && !options.selectedTagIds.every((tagId) => mission.tags?.some((tag) => tag.id === tagId))) return false;
      if (mission.upcomingPeriod) return options.showUpcomingMissions;
      if (options.onlyAcceptableMissions && (mission.remaining ?? 0) <= 0) return false;
      return true;
    })
    .sort((left, right) => {
      if (!!left.upcomingPeriod !== !!right.upcomingPeriod) return left.upcomingPeriod ? 1 : -1;
      const leftDate = (left.upcomingPeriod ? left.upcomingPeriodStartDate : left.closingDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const rightDate = (right.upcomingPeriod ? right.upcomingPeriodStartDate : right.closingDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return leftDate - rightDate || (left.name ?? '').localeCompare(right.name ?? '');
    });
};

export const isRemoteBranchSelectable = (branch: { availabilityBlocked?: boolean; remaining?: number }): boolean =>
  branch.availabilityBlocked !== true && (branch.remaining ?? 0) > 0;

export const requiresRemotePhoneCall = (requiresPhoneCall: boolean | undefined): boolean => requiresPhoneCall === true;

export const getAtHomeLaunchQuestion = (mission: {
  locationType?: number;
  remoteActivity?: number;
  requiresPhoneCall?: boolean;
  requiresEmail?: boolean;
}): string | undefined => {
  if (mission.locationType !== 1) return undefined;
  if (mission.requiresPhoneCall && mission.requiresEmail) return 'at-home-call-email-completed-question';
  if (mission.requiresPhoneCall) return 'at-home-call-completed-question';
  if (mission.requiresEmail) return 'at-home-email-completed-question';
  return mission.remoteActivity === 2 ? 'at-home-activity-completed-question' : 'remote-prevalidation-question';
};

export interface AtHomeContacts {
  phone?: string;
  email?: string;
  website?: string;
}

export interface AtHomeContactRequirements {
  requiresPhoneCall?: boolean;
  requiresEmail?: boolean;
}

export const getAtHomeContactLinks = (contacts: AtHomeContacts, requirements: AtHomeContactRequirements) => {
  const result: { kind: 'phone' | 'email' | 'website'; label: string; value: string; url: string }[] = [];
  const phone = contacts.phone?.trim();
  const email = contacts.email?.trim();
  if (requirements.requiresPhoneCall === true && phone) {
    result.push({ kind: 'phone', label: 'at-home-call-action', value: phone, url: `tel:${encodeURIComponent(phone)}` });
  }
  if (requirements.requiresEmail === true && email && /^[^\s@]+@[^\s@]+$/.test(email)) {
    result.push({ kind: 'email', label: 'at-home-email-action', value: email, url: `mailto:${encodeURIComponent(email)}` });
  }
  const rawWebsite = contacts.website?.trim();
  if (rawWebsite) {
    try {
      const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(rawWebsite) ? rawWebsite : `https://${rawWebsite}`);
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        result.push({ kind: 'website', label: 'at-home-website-action', value: rawWebsite, url: url.toString() });
      }
    } catch { /* Invalid optional links are omitted. */ }
  }
  return result;
};
